import React, {createContext, FC, useCallback, useContext, useEffect, useMemo, useRef, useState,} from "react";
import {ICardItem} from "../../models/payment/ICard";
import {PaymentType} from "../../models/payment/IPay";
import {useHistory, useLocation, useParams} from "react-router-dom";
import {useUi} from "../ui/UIContext";
import {CommandTableApi} from "../../services/Api/commandTable/CommandTableApi";
import {
    SharedExtract,
    SharedExtractControl,
    SharedExtractResponse,
    UserOrderControl
} from "../../models/table/ITableExtract";
import UseFingerPrint from "../payment/UseFingerPrint";
import {IPixInfo} from "../../models/payment/IPixInfo";
import CardApi from "../../services/Api/card/CardApi";
import {IConfirmarPagamento} from "../../models/payment/IPaymentApi";
import {UseMercadoPago} from "../payment/UseMercadoPago";
import {PaymentApi} from "../../services/Api/payment/PaymentApi";
import {useAuth} from "../auth/AuthContext";
import {IProductsPaymentRequest} from "models/table/IProductsPaymentRequest";

interface ITablePaymentContext {
    sharedExtracts: SharedExtractResponse | null;
    extractsToPay: SharedExtractControl[];
    loading: boolean;
    totalAmount: number;
    totalAmountToPay: number;
    initialLettersByUsers: string[],
    paymentType: PaymentType;
    hasAnyChecked: boolean;
    hasSelectedCard: boolean;
    openMenu: boolean;
    pixValues: IPixInfo | null;
    card: ICardItem | null;
    cards: ICardItem[] | null;
    companyId: string;
    openAddCard: boolean;
    openVerifyCard: boolean;
    selectCardOpen: boolean;
    pixTimerRef: any;
    pedidoId: string;
    loadingExtracts: boolean;
    getTotalAmountToPayByUser: (phoneNumber: string) => number;
    getOrderRemainingBalance: (orderControl: UserOrderControl) => number;
    updateExtractToPay: (extract: SharedExtractControl) => void;
    updateTotalWithFee: (feeValue: number) => void;
    updatePaymentMethod: (value: PaymentType) => void;
    setPaymentType: (paymentType: PaymentType) => void;
    setOpenMenu: (openMenu: boolean) => void;
    handleConfirmPayment: () => void;
    onSubmitProducts: () => void;
    onPressDeleteCard: (card: ICardItem) => void;
    setPixValues: (pixValues: IPixInfo) => void;
    setCard: (card: ICardItem) => void;
    setOpenAddCard: (value: boolean) => void;
    setOpenVerifyCard: (value: boolean) => void;
    setSelectCardOpen: (value: boolean) => void;
    getCards: () => void;
    onPaymentConcluded: (pedidoId: string, isPix: boolean) => void;
    paymentValue: number;
    setPaymentValue: (value: number) => void;
    exhibitionPaymentValue: string | number;
    setExhibitionPaymentValue: (value: string | number) => void;
    changeManualPaymentValue: (value: number) => void;
    totalFee: number;
    setTotalFee: (value: number) => void;
}

export interface RouteParams {
    commandId?: string;
    paymentLinkId?: string;
}

const TablePaymentContext = createContext<ITablePaymentContext>({} as ITablePaymentContext);

export const TablePaymentProvider: FC<{ children: React.ReactNode }> = ({children}) => {
    const {loading, showLoading, hideLoading, toastFullScreen, toast} = useUi();
    const history = useHistory();
    const location = useLocation();
    const {commandId, paymentLinkId} = useParams<RouteParams>();
    const {isAuth, loadToken} = useAuth();

    const [sharedExtracts, setSharedExtracts] = useState<SharedExtractResponse | null>(null);
    const [extractsToPay, setExtractsToPay] = useState<SharedExtractControl[]>([]);
    const [totalFee, setTotalFee] = useState<number>(0);
    const [paymentType, setPaymentType] = useState<PaymentType>(PaymentType.NAO_INFORMADO);
    const [companyId, setCompanyId] = useState<string>("");
    const [pixValues, setPixValues] = useState<IPixInfo | null>(null);
    const [openMenu, setOpenMenu] = useState(false);
    const [card, setCard] = useState<ICardItem | null>(null);
    const [cards, setCards] = useState<ICardItem[] | null>(null);
    const [selectCardOpen, setSelectCardOpen] = useState(false);
    const [openAddCard, setOpenAddCard] = useState(false);
    const [openVerifyCard, setOpenVerifyCard] = useState(false);
    const pixTimerRef = useRef<NodeJS.Timeout | null>(null);
    const [paymentValue, setPaymentValue] = useState<number>(0);
    const [exhibitionPaymentValue, setExhibitionPaymentValue] = useState<string | number>("0,00");
    const [loadingExtracts, setLoadingExtracts] = useState<boolean>(false);
    const [pedidoId, setPedidoId] = useState<string>("");

    const {deviceIdMercadoPago} = UseMercadoPago();
    const {fingerPrint} = UseFingerPrint();

    const getNameInitials = (name?: string | null): string => {
        if (name === null || name === undefined) {
            return "";
        }
        const trimmed = name.trim();
        if (trimmed.length === 0) {
            return "";
        }
        const parts = trimmed.split(/\s+/).filter(Boolean);
        if (parts.length === 0) {
            return "";
        }
        if (parts.length === 1) {
            return parts[0][0].toUpperCase();
        }
        const firstInitial = parts[0][0];
        const lastInitial = parts[parts.length - 1][0];
        return (firstInitial + lastInitial).toUpperCase();
    };

    const totalAmount = useMemo(() => {
        if (!sharedExtracts) {
            return 0;
        }
        let lTotal = sharedExtracts?.command.remainingBalance ?? 0;
        if (totalFee > 0) {
            return lTotal + totalFee;
        }
        return lTotal;
    }, [sharedExtracts, totalFee]);

    const totalAmountToPay = useMemo(() => {
        if (!extractsToPay && !paymentValue) {
            return 0;
        }
        if (paymentValue !== 0) {
            return Number(paymentValue);
        }
        return 0;
    }, [extractsToPay, paymentValue]);

    const getTotalAmountToPayByUser  = useCallback((userPhoneNumber: string) => {
        const userExtract = extractsToPay
            .find(us => us.userData.phoneNumber === userPhoneNumber);
        if (!extractsToPay || !userExtract) return 0;
        return userExtract.userOrders
            .filter(order => order.checked)
            .reduce((sum, order) => sum + (order.cost * order.quantity), 0)
    }, [extractsToPay])

    const getOrderRemainingBalance = useCallback((order: UserOrderControl) => {
        return order.cost * order.quantity;
    }, []);

    const initialLettersByUsers = useMemo(() => {
        if (sharedExtracts && sharedExtracts.extracts) {
            const sharedExtractsUsersName = sharedExtracts.extracts.map(extract => extract.userData.name);
            const visibleUsernames = sharedExtractsUsersName.length > 3
                ? sharedExtractsUsersName.slice(0, 2) : sharedExtractsUsersName;
            return visibleUsernames.map(it => getNameInitials(it))
        }
        return [];
    }, [sharedExtracts]);

    const hasAnyChecked = useMemo(() => {
        return extractsToPay.some(extract => extract.userOrders.some(it => it.checked))
    }, [extractsToPay])

    const hasSelectedCard = useMemo(() => {
        return paymentType === PaymentType.CREDITO ? !!card : true;
    }, [card, paymentType])

    const changeManualPaymentValue = useCallback((value: number) => {
        let roundedValue = Math.round(value * 100) / 100;
        setPaymentValue(roundedValue);
        if(roundedValue >= totalAmount) {
            setPaymentValue(totalAmount);
            roundedValue = totalAmount;
        }
        setExhibitionPaymentValue(roundedValue.toLocaleString("pt-BR", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }));
    }, [totalAmount]);

    const updateExtractToPay = useCallback((extract: SharedExtractControl) => {
        setExtractsToPay(prev => {
            const index = prev.findIndex(it => it.userData.name === extract.userData.name);
            if (index === -1) {
                return [...prev, extract];
            }
            const newExtracts = [...prev];
            newExtracts[index] = extract;
            const updatedExtractSum = newExtracts
                .flatMap(extract => extract.userOrders)
                .filter(it => it.checked)
                .reduce((sum, value) => sum + (value.quantity *  value.cost), 0)
            ;
            changeManualPaymentValue(updatedExtractSum + totalFee);
            return newExtracts;
        });
    }, [changeManualPaymentValue, totalFee]);

    const updateTotalWithFee = useCallback((feeValue: number) => {
        if (feeValue > 0) {
            const total = paymentValue + feeValue;
            setPaymentValue(total);
            setExhibitionPaymentValue(total.toLocaleString("pt-BR", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            }));
        } else {
            const total = paymentValue - (sharedExtracts?.command.totalFee ?? 0);
            setPaymentValue(total);
            setExhibitionPaymentValue(total.toLocaleString("pt-BR", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            }));
        }
    }, [paymentValue, sharedExtracts]);

    const getTableExtract = useCallback(async (paymentLinkId: string) => {
        if (!commandId) {
            return;
        }
        showLoading();
        setLoadingExtracts(true);
        try {
            const response = await CommandTableApi().getTableExtract(commandId, paymentLinkId);
            const data = response.data.value;
            if (response && data) {
                const cloneSharedExtract = data.extracts.map((it: SharedExtract) => {
                    return {
                        ...it,
                        userOrders: it.userOrders.map(us => {
                            return {
                                ...us,
                                quantity: 1,
                                totalQuantity: us.quantity,
                                remainingBalance: us.cost,
                                checked: false
                            }
                        })
                    }
                });
                setCompanyId(data.companyId);
                setSharedExtracts(data);
                setTotalFee(data.command.totalFee ?? 0);
                // changeManualPaymentValue(data.command.totalFee ?? 0);
                setExtractsToPay(cloneSharedExtract);
            }
        } catch (error: any) {
            if (error?.response?.data?.message) {
                toastFullScreen(error?.response?.data?.message, "error", "error", 0);
                return;
            }
            toastFullScreen(
                'Não foi possível encontrar o extrato da comanda',
                "error",
                "error",
                0
            )
        } finally {
            setLoadingExtracts(false);
            hideLoading();
        }
    }, [commandId, hideLoading, showLoading, toastFullScreen]);

    const onSubmitProducts = useCallback(async () => {
        if (commandId && paymentLinkId) {
            showLoading();
            try {
                const data: IProductsPaymentRequest = { amount: paymentValue };
                const response = await CommandTableApi().generateReservationPayment(data, commandId, paymentLinkId);
                if (response?.data) {
                    setPedidoId(response.data.value);
                }
                return response;
            } catch (error: any) {
                if (error?.response?.data?.message) {
                    toastFullScreen(error?.response?.data?.message, "error", "error", 3000);
                    return;
                }
                toastFullScreen(
                    "Pedido Não Encontrado",
                    "error",
                    "error",
                    3000
                );
            } finally {
                hideLoading();
            }
        }
    }, [commandId, hideLoading, paymentValue, paymentLinkId, showLoading, toastFullScreen]);

    const onConfirmPayment = useCallback(async (pedidoId: string) => {
        const isPaymentPix = paymentType === PaymentType.PIX;
        const paymentObject: IConfirmarPagamento = {
            localClienteId: companyId,
            customDeliveryDescription: "",
            tipoPagamentoId: isPaymentPix ? PaymentType.DEBITO : paymentType,
            troco: 0,
            parcelas: 1,
            dadosCartaoCadastrado: card ? {cartaoId: card.id, codigoSeguranca: ""} : undefined,
            resumoPedido: {
                pedidoId: pedidoId,
                valorPedido: totalAmountToPay
            },
            fingerPrint: deviceIdMercadoPago,
            canalVenda: 1,
            deviceFingerPrint: fingerPrint?.visitorId,
            gerarPIX: isPaymentPix
        };
        try {
            showLoading();
            const response = await PaymentApi().confirmCommandPayment(paymentObject);
            if (response) {
                if (response.data.qrcodeBase64 && response.data.qrcodeLink) {
                    setPixValues({
                        value: totalAmountToPay,
                        qrCodeLink: response.data.qrcodeLink,
                        qrCodeBase64: response.data.qrcodeBase64
                    })
                    history.push({
                        pathname:`${location.pathname}/pix`,
                        search: `?pedidoId=${pedidoId}`
                    });
                    return;
                }
                history.replace({
                    pathname: `${location.pathname}/pagamento-sucesso`,
                    search: `?pedidoId=${pedidoId}`
                });
            }
        } catch (error: any) {
            if (error?.response?.data?.message) {
                toast(error?.response?.data?.message, "error");
            }
        } finally {
            hideLoading();
        }
    }, [card, companyId, deviceIdMercadoPago, fingerPrint, hideLoading, history,
        location.pathname, paymentType, showLoading, toast, totalAmountToPay])

    const handleConfirmPayment = useCallback(async () => {
        const response = await onSubmitProducts();
        if (response?.data) {
            await onConfirmPayment(response.data.value);
        }
    }, [onConfirmPayment, onSubmitProducts])

    const onPaymentConcluded = useCallback((pedidoId: string, isPix: boolean) => {
        if (pixTimerRef.current) {
            clearInterval(pixTimerRef.current);
            pixTimerRef.current = null;
        }
        history.replace({
            pathname: `${location.pathname}/pagamento-sucesso`,
            search: `?pedidoId=${pedidoId}&isPix=${isPix}`
        });
    }, [history, location.pathname]);

    const getCards = useCallback(async () => {
        showLoading();
        try {
            const response = await CardApi.getCards();
            if (response.data) {
                setCards(response.data);
            }
        } catch (error: any) {
            toast(error?.response?.data?.message ?? "Não foi possível recuperar os cartões salvos.", "error");
        } finally {
            hideLoading();
        }
    }, [hideLoading, showLoading, toast]);

    const updatePaymentMethod = useCallback(async (paymentType: PaymentType) => {
        setPaymentType(paymentType);
        if (paymentType === PaymentType.CREDITO) {
            await getCards();
            setSelectCardOpen(true);
            return;
        }
        setCard && setCard(null);
        setSelectCardOpen(false);
    }, [getCards])

    const onPressDeleteCard = useCallback(async (_deletedCard: ICardItem) => {
        showLoading();
        try {
            await CardApi.deleteCard(_deletedCard.id);
            if (_deletedCard.id === card?.id) {
                setCard && setCard(null);
                setPaymentType(PaymentType.NAO_INFORMADO);
            }
            await getCards();
        } catch (error: any) {
            toast(error?.response?.data?.message ?? "Ops, não foi possivel excluir cartao", "error");
        } finally {
            hideLoading();
        }
    }, [card, getCards, hideLoading, showLoading, toast]);

    useEffect(() => {
        window.scrollTo({top: 0, behavior: "smooth"});
        if (!loadToken || !isAuth) {
            return;
        }
        if (paymentLinkId && commandId) {
            getTableExtract(paymentLinkId).then();
            return;
        }
        toastFullScreen(
            "Extrato da mesa não encontrado",
            "error",
            "error",
            0
        );
    }, [commandId, getTableExtract, history, isAuth, loadToken, location.pathname, location.search, paymentLinkId, toastFullScreen]);

    useEffect(() => {
        if (sharedExtracts && sharedExtracts.command) {
            changeManualPaymentValue(sharedExtracts.command.totalFee ?? 0);
        }
    }, [sharedExtracts]);

    return (
        <TablePaymentContext.Provider
            value={{
                loading,
                sharedExtracts,
                extractsToPay,
                updateExtractToPay,
                updateTotalWithFee,
                totalAmount,
                totalAmountToPay,
                initialLettersByUsers,
                paymentType,
                updatePaymentMethod,
                hasAnyChecked,
                openMenu,
                pixValues,
                card,
                cards,
                openVerifyCard,
                openAddCard,
                selectCardOpen,
                loadingExtracts,
                getTotalAmountToPayByUser,
                getOrderRemainingBalance,
                setPixValues,
                setPaymentType,
                setOpenVerifyCard,
                setCard,
                onPressDeleteCard,
                setOpenAddCard,
                getCards,
                setSelectCardOpen,
                setOpenMenu,
                handleConfirmPayment,
                hasSelectedCard,
                pixTimerRef,
                onPaymentConcluded,
                paymentValue,
                setPaymentValue,
                exhibitionPaymentValue,
                setExhibitionPaymentValue,
                changeManualPaymentValue,
                totalFee,
                setTotalFee,
                onSubmitProducts,
                pedidoId,
                companyId,
            }}
        >
            {children}
        </TablePaymentContext.Provider>
    );
};

export const useTablePayment = () => {
    return useContext(TablePaymentContext);
};
